import React, { forwardRef } from "react";
import { useBookingEditText } from "../text";
import moment from "moment";
import { GoTriangleDown } from "react-icons/go";
import Select, { components } from "react-select";
import SelectionButton from "./SelectionButton";
import PassengerDropdown from "./PassengerDropdown";
import LuggageDropdown from "./LuggageDropdown";
import UsageDurationDropdown from "./UsageDurationDropdown";
import TravelDatePicker from "./TravelDatePicker";
import PickupTimePicker from "./PickupTimePicker";
import LongDistanceFeeField from "./LongDistanceFeeField";
import CustomGoogleAutoComplete from "../../../../components/layout-new-theme/google-input/CustomGoogleAutoComplete";
import { BsPlusCircle } from "react-icons/bs";
import { cn } from "../../../../utils/classnames";
import { createTypographyHelper } from "../../../../utils/typography";

const BasicInformationSection = forwardRef(({
  duplicateDropoffIds = new Set(),
  transferType,
  setTransferType,
  pickupLocationSelected,
  pickupLocation,
  pickupLocationData,
  dropoffLocationSelected,
  dropoffLocation,
  dropoffLocationData,
  airportDropoffLocations,
  airportDropoffLocationSelected,
  handleAddDestination,
  handleRemoveDestination,
  LocationPinIcon,
  duration,
  setDuration,
  durationOptions,
  travelDate,
  setTravelDate,
  setFormatedTravelDate,
  pickupTime,
  setPickupTime,
  travelArea,
  setTravelArea,
  formatTravelArea,
  travelAreaOptions,
  passengers,
  setPassengers,
  luggage,
  setLuggage,
  longDistanceFeeType,
  setLongDistanceFeeType,
  longDistanceFeeValue,
  setLongDistanceFeeValue,
}, ref) => {
  const { t, language } = useBookingEditText();
  const getTypographyClass = createTypographyHelper(language);

  // The first dropoff duplicating the pickup also highlights the pickup field.
  const firstDropoff = airportDropoffLocations?.[0];
  const hasDuplicateLocation =
    Boolean(firstDropoff) && duplicateDropoffIds.has(firstDropoff.id);

  return (
    <section ref={ref} className="tw:!mb-4 tw:!bg-white">
      <div className="tw:!px-5 tw:!pt-5 tw:!pb-2">
        <div className="tw:!mb-0 tw:!text-[#282828] tw:!text-[16px] tw:!font-[600] tw:font-hanken-grotesk">
          {t("editBooking.BookingEditPage.basicInformation.title")}
        </div>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5 tw:lg:p-6">
        <div className="tw:flex tw:flex-col">
          <div className={`tw:mb-2 tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
            {t("editBooking.BookingEditPage.basicInformation.typeOfTransfer")}
          </div>
          <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-wrap tw:gap-1.5">
            <SelectionButton
              options={[
                { value: "charter", label: t("editBooking.BookingEditPage.basicInformation.bookingType.byTheHour") },
                { value: "airport", label: t("editBooking.BookingEditPage.basicInformation.bookingType.specifiedDestination") },
              ]}
              value={transferType}
              onChange={setTransferType}
              className={getTypographyClass(14)}
            />
          </div>
        </div>

        <div className="tw:grid tw:grid-cols-12 tw:gap-4">
          <div className={transferType === "charter" ? "tw:col-span-12 tw:md:col-span-6" : "tw:col-span-12"}>
            <div className="tw:flex tw:flex-col">
              <div className={`tw:mb-2 tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                {t("editBooking.BookingEditPage.basicInformation.pickupLocation")}
              </div>
              <div className="tw:relative">
                <CustomGoogleAutoComplete
                  id="pickUpAddress"
                  onPlaceSelected={pickupLocationSelected}
                  defaultValue={pickupLocation}
                  placeholder={t("editBooking.BookingEditPage.basicInformation.pickupLocationPlaceholder")}
                  ariaLabel={t("editBooking.BookingEditPage.basicInformation.pickupLocationPlaceholder")}
                  inputClassName={`tw:h-[50px] tw:w-full tw:rounded-[5px] tw:!border ${hasDuplicateLocation ? 'tw:!border-[#C01F12]' : 'tw:!border-[#e8e8e8]'} tw:bg-white tw:!font-[400] tw:px-[14px] tw:outline-none tw:focus:border-[#282828] ${getTypographyClass(14)} tw:font-hanken-grotesk`}
                  inputStyle={{
                    fontSize: "15px",
                    color: pickupLocation ? "#282828" : "#a7a7a7",
                  }}
                />
                <img alt="img" src={LocationPinIcon} className="tw:absolute tw:top-1/2 tw:-translate-y-1/2 tw:left-[14px]" />
              </div>
              {hasDuplicateLocation && (
                <p className="tw:!mb-0 tw:!mt-1 tw:!text-[#C01F12] tw:text-[12px]">
                  {t("editBooking.BookingEditPage.basicInformation.pickupLocationSameValidation")}
                </p>
              )}
            </div>
          </div>
          {transferType === "charter" ? (
            <div className="tw:col-span-12 tw:md:col-span-6">
              <div className="tw:flex tw:flex-col">
                <div className={`tw:mb-2 tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                  {t("editBooking.BookingEditPage.basicInformation.dropoffLocation")}
                </div>
                <div className="tw:relative">
                  <CustomGoogleAutoComplete
                    id="dropoff1"
                    onPlaceSelected={dropoffLocationSelected}
                    defaultValue={dropoffLocation}
                    placeholder={t("editBooking.BookingEditPage.basicInformation.dropoffLocationPlaceholder")}
                    ariaLabel={t("editBooking.BookingEditPage.basicInformation.dropoffLocationPlaceholder")}
                    inputClassName={`tw:h-[50px] tw:w-full tw:rounded-[5px] tw:!border tw:!border-[#e8e8e8] tw:bg-white tw:!font-[400] tw:px-[14px] tw:outline-none tw:focus:border-[#282828] ${getTypographyClass(14)} tw:font-hanken-grotesk`}
                    inputStyle={{
                      // border: "1px solid #E8E8E8 !important",
                      fontSize: "15px",
                      color: dropoffLocation ? "#282828" : "#a7a7a7",
                    }}
                  />
                  <img alt="img" src={LocationPinIcon} className="tw:absolute tw:top-1/2 tw:-translate-y-1/2 tw:left-[14px]" />
                </div>
              </div>
            </div>
          ) : (
            <div className="tw:col-span-12 tw:flex tw:flex-col">
              <div className={`tw:!mb-2 tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                {t("editBooking.BookingEditPage.basicInformation.dropoffLocation")}
              </div>

              {airportDropoffLocations.map((dropoff, index) => (
                <div key={dropoff.id} className={index === airportDropoffLocations.length - 1 ? '' : 'tw:mb-2'}>
                  <div className="tw:relative">
                    <CustomGoogleAutoComplete
                      id={`dropoff-${dropoff.id}`}
                      onPlaceSelected={(place) => airportDropoffLocationSelected(dropoff.id, place)}
                      defaultValue={dropoff.address || null}
                      placeholder={t("editBooking.BookingEditPage.basicInformation.dropoffLocationPlaceholder")}
                      ariaLabel={t("editBooking.BookingEditPage.basicInformation.dropoffLocationPlaceholder")}
                      inputClassName={`tw:h-[50px] tw:w-full tw:rounded-[5px] tw:!border ${duplicateDropoffIds.has(dropoff.id) ? 'tw:!border-[#C01F12]' : 'tw:!border-[#e8e8e8]'} tw:bg-white tw:!font-[400] tw:pl-[40px] tw:pr-[40px] tw:outline-none tw:focus:border-[#282828] ${getTypographyClass(14)} tw:font-hanken-grotesk`}
                      inputStyle={{
                        fontSize: "15px",
                        color: pickupLocation ? "#282828" : "#a7a7a7",
                      }}
                    />
                    <img alt="img" src={LocationPinIcon} className="tw:absolute tw:top-1/2 tw:-translate-y-1/2 tw:left-[14px]" />
                    {airportDropoffLocations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveDestination(dropoff.id)}
                        className={cn(
                          "tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:flex tw:items-center tw:justify-center",
                          "tw:text-[#8d8d8d] tw:hover:text-[#282828]"
                        )}
                        aria-label={`Remove destination ${index + 1}`}
                      >
                        <span className="tw:text-[28px] tw:leading-none">×</span>
                      </button>
                    )}
                  </div>
                  {duplicateDropoffIds.has(dropoff.id) && (
                    <p className="tw:!mb-0 tw:!mt-1 tw:!text-[#C01F12] tw:text-[12px]">
                      {t("editBooking.BookingEditPage.basicInformation.dropoffLocationSameValidation")}
                    </p>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddDestination}
                className={cn(
                  "tw:flex tw:items-center tw:gap-2 tw:w-fit tw:h-[46px] tw:!rounded-[5px] tw:!border tw:!border-[#282828] tw:bg-white tw:px-4 tw:!mt-2",
                  getTypographyClass(13)
                )}
              >
                <BsPlusCircle size={16} color="#282828" />
                <span className="tw:text-[#282828] tw:!text-[15px] tw:!font-[400]">{t("editBooking.BookingEditPage.basicInformation.addDropoffLocation")}</span>
              </button>
            </div>
          )}
        </div>

        <div className="tw:grid tw:grid-cols-12 tw:gap-4">
          {transferType === "charter" && (
            <div className="tw:col-span-12 tw:md:col-span-4">
              <UsageDurationDropdown
                value={duration}
                onChange={(selectedOption) => setDuration(selectedOption)}
                options={durationOptions}
              />
            </div>
          )}
          <div
            className={
              transferType === "charter"
                ? "tw:col-span-12 tw:md:col-span-4"
                : "tw:col-span-12 tw:md:col-span-3"
            }
          >
            <TravelDatePicker
              value={travelDate}
              onSelect={(date) => {
                setTravelDate(date);
                const formattedDate = moment(date).format("YYYY-MM-DD");
                setFormatedTravelDate(formattedDate);
              }}
            />
          </div>
          <div
            className={
              transferType === "charter"
                ? "tw:col-span-12 tw:md:col-span-4"
                : "tw:col-span-12 tw:md:col-span-3"
            }
          >
            <PickupTimePicker
              value={pickupTime}
              onChange={(date) => {
                setPickupTime(date);
              }}
            />
          </div>
          {transferType === "charter" && (
            <div className="tw:col-span-12 tw:md:col-span-4">
              <div className="tw:flex tw:flex-col">
                <div className={`tw:mb-2 tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                  {t("editBooking.BookingEditPage.basicInformation.travelArea")}
                </div>
                <Select
                  placeholder={t("ByTheHour.TravelArea.Title")}
                  classNamePrefix="react-select-travel-area"
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      height: "50px",
                      borderRadius: "5px",
                      border: "1px solid #e8e8e8",
                      backgroundColor: "#ffffff",
                      fontSize: "14px",
                      paddingLeft: "36px",
                      boxShadow: "none",
                      "&:hover": {
                        border: "1px solid #e8e8e8",
                      },
                      fontWeight: "normal",
                    }),
                    valueContainer: (baseStyles) => ({
                      ...baseStyles,
                      paddingLeft: "14px",
                      fontWeight: "normal",
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      borderRadius: "5px",
                      marginTop: "4px",
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      fontSize: "14px",
                      backgroundColor: state.isSelected ? "#dbbe78" : state.isFocused ? "#eddfbb" : "#ffffff",
                      color: state.isSelected ? "#ffffff" : "#282828",
                      cursor: "pointer",
                    }),
                    singleValue: (baseStyles) => ({
                      ...baseStyles,
                      fontWeight: "normal",
                    }),
                  }}
                  autoComplete="off"
                  onChange={(option) => setTravelArea(option?.value || "")}
                  getOptionLabel={(option) => t(option.label)}
                  value={formatTravelArea(travelArea)}
                  options={travelAreaOptions}
                  components={{
                    DropdownIndicator: (props) => (
                      <components.DropdownIndicator {...props}>
                        <div className="tw:pr-2">
                          <GoTriangleDown color="#DBBE78" size={16} />
                        </div>
                      </components.DropdownIndicator>
                    ),
                    IndicatorSeparator: () => null,
                  }}
                />
              </div>
            </div>
          )}
          <div
            className={
              transferType === "charter"
                ? "tw:col-span-12 tw:md:col-span-4"
                : "tw:col-span-12 tw:md:col-span-3"
            }
          >
            <PassengerDropdown
              passengers={passengers}
              setPassengers={setPassengers}
              label={t("editBooking.BookingEditPage.basicInformation.passengers")}
            />
          </div>
          <div
            className={
              transferType === "charter"
                ? "tw:col-span-12 tw:md:col-span-4"
                : "tw:col-span-12 tw:md:col-span-3"
            }
          >
            <LuggageDropdown
              luggage={luggage}
              setLuggage={setLuggage}
              label={t("editBooking.BookingEditPage.basicInformation.luggage")}
            />
          </div>
        </div>

        <LongDistanceFeeField
          feeType={longDistanceFeeType}
          setFeeType={setLongDistanceFeeType}
          feeValue={longDistanceFeeValue}
          setFeeValue={setLongDistanceFeeValue}
        />
      </div>
    </section>
  );
});

export default BasicInformationSection;
